import { queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { STALE_TIME } from '@/api/staleTimes';
import {
  effectivePermissionListSchema,
  permissionItemListSchema,
  userRoleItemListSchema,
  type EffectivePermission,
  type PermissionItem,
  type UserRoleItem,
} from '../types/permission.types';

export const permissionQueryKeys = {
  all: () => ['rbac'] as const,
  catalogue: (includeInactive: boolean) =>
    [...permissionQueryKeys.all(), 'catalogue', includeInactive] as const,
  rolePermissions: (roleId: number) =>
    [...permissionQueryKeys.all(), 'role-permissions', roleId] as const,
  userRoles: (userId: number) => [...permissionQueryKeys.all(), 'user-roles', userId] as const,
  userPermissions: (userId: number) =>
    [...permissionQueryKeys.all(), 'user-permissions', userId] as const,
  userDirectPermissions: (userId: number) =>
    [...permissionQueryKeys.all(), 'user-direct-permissions', userId] as const,
};

export const permissionCatalogueQueryOptions = (includeInactive = false) =>
  queryOptions({
    queryKey: permissionQueryKeys.catalogue(includeInactive),
    queryFn: async (): Promise<PermissionItem[]> => {
      const res = await apiClient.get(API.permission.catalogue, {
        params: includeInactive ? { includeInactive: true } : undefined,
      });
      return permissionItemListSchema.parse(res.data);
    },
    staleTime: STALE_TIME,
  });

export const rolePermissionsQueryOptions = (roleId: number | undefined) =>
  queryOptions({
    queryKey: permissionQueryKeys.rolePermissions(roleId ?? 0),
    queryFn: async (): Promise<PermissionItem[]> => {
      const res = await apiClient.get(API.role.permissions(roleId!));
      return permissionItemListSchema.parse(res.data);
    },
    enabled: roleId !== undefined,
    staleTime: STALE_TIME,
  });

export async function setRolePermissions(roleId: number, permissionIds: number[]): Promise<void> {
  await apiClient.put(API.role.setPermissions(roleId), { permissionIds });
}

export const userRolesQueryOptions = (userId: number | undefined) =>
  queryOptions({
    queryKey: permissionQueryKeys.userRoles(userId ?? 0),
    queryFn: async (): Promise<UserRoleItem[]> => {
      const res = await apiClient.get(API.permission.userRoles(userId!));
      return userRoleItemListSchema.parse(res.data);
    },
    enabled: userId !== undefined,
    staleTime: STALE_TIME,
  });

export async function setUserRoles(userId: number, roleIds: number[]): Promise<void> {
  await apiClient.put(API.permission.setUserRoles(userId), { roleIds });
}

export const userPermissionsQueryOptions = (userId: number | undefined) =>
  queryOptions({
    queryKey: permissionQueryKeys.userPermissions(userId ?? 0),
    queryFn: async (): Promise<EffectivePermission[]> => {
      const res = await apiClient.get(API.permission.userPermissions(userId!));
      return effectivePermissionListSchema.parse(res.data);
    },
    enabled: userId !== undefined,
    staleTime: STALE_TIME,
  });

export const userDirectPermissionsQueryOptions = (userId: number | undefined) =>
  queryOptions({
    queryKey: permissionQueryKeys.userDirectPermissions(userId ?? 0),
    queryFn: async (): Promise<PermissionItem[]> => {
      const res = await apiClient.get(API.permission.userDirectPermissions(userId!));
      return permissionItemListSchema.parse(res.data);
    },
    enabled: userId !== undefined,
    staleTime: STALE_TIME,
  });

export async function setUserPermissions(userId: number, permissionIds: number[]): Promise<void> {
  await apiClient.put(API.permission.setUserPermissions(userId), { permissionIds });
}
